import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IPriceBookSimple, IPriceBook, IPriceBookUpdateDTO, IPriceBookViewDTO, IPriceBookType, PriceBookTypeValue, ICondition } from '../models/pricebook.model';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IPageCount } from 'src/app/shared/models/page.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PricebookService {
  constructor(private http: HttpClient, private translateService: TranslateService) {}

  public getPricebooks(page: IPageCount, key?: string | undefined): Observable<APIResponse<IPriceBookSimple>> {
    const httpParams = new HttpParams().set('key', key!).set('page', page.number).set('limit', page.size);
    return this.http.get<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs`, { params: httpParams });
  }

  public getPricebookById(id: number): Observable<APIResponse<IPriceBookViewDTO>> {
    return this.http.get<APIResponse<IPriceBookViewDTO>>(`${environment.BASE_URL_API}loyalty-programs/${id}`);
  }

  public create(pricebook: IPriceBook): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.post<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs`, pricebook);
  }

  public update(pricebook: IPriceBookUpdateDTO): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.post<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/${pricebook.id}`, pricebook);
  }

  public delete(id: number): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.delete<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/${id}`);
  }

  public bulkDelete(ids: number[]): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.post<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/bulk-delete`, { ids });
  }

  public enable(ids: number[]): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.put<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/bulk/activate`, { ids });
  }

  public disable(ids: number[]): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.put<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/bulk/deactivate`, { ids });
  }

  public togglePricebookForCatalog(catalogId: number, pricebookId: number): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(`${environment.BASE_URL_API}loyalty-programs/${pricebookId}/toggle-pricelist/${catalogId}`, {});
  }

  public getPricebooksForLocation(locationId: number): Observable<APIResponse<IPriceBookSimple>> {
    return this.http.get<APIResponse<IPriceBookSimple>>(`${environment.BASE_URL_API}loyalty-programs/location/${locationId}`);
  }

  public getPricebookTypes(): IPriceBookType[] {
    return [
      // {
      //   icon: 'percentage-cc',
      //   label: 'Catalog Discount Override',
      //   description: 'Apply a new Discount on MSRP (Retail Price) for Customers buying a selection of Products.',
      //   value: 'catalog_discount_override',
      // },
      {
        icon: 'percentage-cc',
        label: this.translateService.instant('pricebook.calculation_types.extra_discount_on_products'),
        description: this.translateService.instant('pricebook.calculation_types_description.extra_discount_on_products'),
        value: 'extra_discount_on_products',
        category: 'pricing_rule',
      },
      // {
      //   icon: 'currency',
      //   label: 'Product Final Price Override',
      //   description: 'Apply a new Final price for Customers buying a particular Product.',
      //   value: 'final_price_override',
      // },
      {
        icon: 'percentage-cc',
        label: this.translateService.instant('pricebook.calculation_types.extra_discount_on_full_order'),
        description: this.translateService.instant('pricebook.calculation_types_description.extra_discount_on_full_order'),
        value: 'extra_discount_on_full_order',
        category: 'promotion',
      },
      // {
      //   icon: 'gift',
      //   label: 'Product Gift',
      //   description: 'Define a Product to be given as a gift (Specific Product & Quantity) for Customers meeting an Order Condition.',
      //   value: 'product_gift',
      //   type: "promotion"
      // },
      {
        icon: 'gift',
        label: this.translateService.instant('pricebook.calculation_types.buy_x_get_y'),
        description: this.translateService.instant('pricebook.calculation_types_description.buy_x_get_y'),
        value: 'buy_x_get_y',
        category: "promotion"
      },
      {
        icon: 'gift',
        label: this.translateService.instant('pricebook.calculation_types.free_shipping'),
        description: this.translateService.instant('pricebook.calculation_types_description.free_shipping'),
        value: 'free_shipping',
        category: "promotion"
      },
      // {
      //   icon: 'currency',
      //   label: 'Cashback',
      //   description: 'Unlock a monetary value to be saved in Store Credit for Customers meeting an Order Condition (such as Min. Order Value)',
      //   value: 'cashback',
      //   type: "promotion"
      // },
    ];
  }

  public getPricebookCategoryFromCalculationType(type: PriceBookTypeValue) {
    switch (type) {
      case 'extra_discount_on_products':
        return 'pricing_rule';
      case 'extra_discount_on_full_order':
        return 'promotion';
      default:
        return 'promotion';
    }
  }

  public getPricebookCalculationTypeDisplayText(type: PriceBookTypeValue) {
    const types = this.getPricebookTypes();
    const found = types.find((item) => item.value === type);
    return found ? found.label : '--';
  }

  /**
   * Maps tierd_logic_based_on value to the correct translation key suffix
   * based on the calculation type
   */
  private getTierdLogicTranslationKeySuffix(tierdLogicBasedOn: string, calculationType: string): string {
    if (calculationType === 'extra_discount_on_products') {
      switch (tierdLogicBasedOn) {
        case 'order_value':
          return 'amount';
        case 'order_quantity':
          return 'quantity';
        case 'total_order_amount':
          return 'total_amount';
        case 'total_order_quantity':
          return 'total_quantity';
        default:
          return 'amount';
      }
    } else {
      // For extra_discount_on_full_order, total_order_amount and total_order_quantity
      // map to the same as order_value and order_quantity
      switch (tierdLogicBasedOn) {
        case 'order_value':
        case 'total_order_amount':
          return 'amount';
        case 'order_quantity':
        case 'total_order_quantity':
          return 'quantity';
        default:
          return 'amount';
      }
    }
  }

  public getRewardMessage(
    pricebook: IPriceBookViewDTO,
    amount: number | null = null,
    quantity: number | null = null,
  ): { pricebook: string; message: string; parameters: any } {
    let message = {
      translationKey: '',
      parameters: {},
    };
    let type: PriceBookTypeValue = pricebook.calculation_type;

    const hasConditionOnOrderValue =
      pricebook.conditions &&
      pricebook.conditions.some((condition) => condition.condition === 'min_order_value');

    if (type === 'extra_discount_on_products' || type === 'extra_discount_on_full_order') {
      let extraDiscount: number | string = pricebook.extra_discount;

      message.translationKey =
        type === 'extra_discount_on_products'
          ? 'pricebook.rewards.extra_discount_on_products'
          : 'pricebook.rewards.extra_discount_on_full_order';

      message.parameters = {
        extra_discount: extraDiscount,
      };

      const hasThresholds = pricebook.thresholds && pricebook.thresholds.length > 0;
      const hasConditionOnOrderValue =
        pricebook.conditions &&
        pricebook.conditions.some((condition) => condition.condition === 'min_order_value');
      const isAmountBased = ['order_value', 'total_order_amount'].includes(pricebook.tierd_logic_based_on);

      if (hasThresholds) {
        let thresholdBasisValue =
          isAmountBased ? amount : quantity;
        // Find the threshold with the maximum extra_discount
        const maxDiscountThreshold = pricebook.thresholds.reduce((max, threshold) => {
          return Number(threshold.new_discount) > Number(max.new_discount) ? threshold : max;
        }, pricebook.thresholds[0]);

        const translationKeySuffix = this.getTierdLogicTranslationKeySuffix(
          pricebook.tierd_logic_based_on,
          type
        );

        message.translationKey =
          'pricebook.rewards.tiered_extra_discount.generic.' +
          (type === 'extra_discount_on_full_order' ? 'full_order' : 'products') +
          '.' +
          translationKeySuffix;
        message.parameters = {
          extra_discount: maxDiscountThreshold.new_discount,
        };

        // If the threshold basis value is greater than or equal to the minimum order value of the maximum discount threshold, show the max threshold reached message
        if (
          thresholdBasisValue &&
          thresholdBasisValue >=
            (isAmountBased
              ? maxDiscountThreshold.min_order_value
              : maxDiscountThreshold.min_order_quantity!)
        ) {
          message.translationKey =
            'pricebook.rewards.tiered_extra_discount.max_threshold_reached' +
            '.' +
            (isAmountBased ? 'amount' : 'quantity');
          message.parameters = {
            extra_discount: maxDiscountThreshold.new_discount,
            current_threshold_value: isAmountBased
              ? maxDiscountThreshold.min_order_value
              : maxDiscountThreshold.min_order_quantity,
          };
        } else {
          // Try to find the threshold that is the closest to the threshold basis value
          const currentPossibleThresholdIndex = pricebook.thresholds.findIndex((threshold) =>
            isAmountBased
              ? threshold.max_order_value !== null &&
                thresholdBasisValue &&
                thresholdBasisValue >= threshold.min_order_value &&
                thresholdBasisValue <= threshold.max_order_value
              : threshold.max_order_quantity !== null &&
                thresholdBasisValue &&
                thresholdBasisValue >= threshold.min_order_quantity! &&
                thresholdBasisValue <= threshold.max_order_quantity!,
          );

          if (currentPossibleThresholdIndex !== -1) {
            const currentThreshold = pricebook.thresholds[currentPossibleThresholdIndex];
            const nextThreshold = pricebook.thresholds[currentPossibleThresholdIndex + 1];
            message.translationKey =
              'pricebook.rewards.tiered_extra_discount.threshold_reached' +
              '.' +
              (isAmountBased ? 'amount' : 'quantity');
            message.parameters = {
              extra_discount: currentThreshold.new_discount,
              current_threshold_value: isAmountBased
                ? currentThreshold.min_order_value
                : currentThreshold.min_order_quantity,
              next_threshold_value: isAmountBased
                ? nextThreshold?.min_order_value
                : nextThreshold.min_order_quantity,
            };
          }
        }
      }

      if (hasConditionOnOrderValue && !hasThresholds) {
        const minOrderValue = pricebook.conditions.find(
          (condition) => condition.condition === 'min_order_value',
        )?.condition_value as number;

        message.translationKey = 'pricebook.rewards.extra_discount_on_full_order_with_condition';
        message.parameters = {
          extra_discount: extraDiscount,
          min_order_value: minOrderValue,
        };
      }
    } else if (type === 'buy_x_get_y') {
      const rewardScope = pricebook.product_reward_scope;
      if (rewardScope === 'product_as_a_gift') {
        message.translationKey = 'pricebook.rewards.buy_x_get_y_as_a_gift';
        message.parameters = {
          products: pricebook.reward_products
            .map((product) => `${product.name} - ${product.quantity} Units`)
            .join(', '),
          min_for_reward: pricebook.min_quantity_for_reward || pricebook.min_value_for_reward,
          min_for_reward_type: pricebook.min_quantity_for_reward ? 'Units' : '€',
        };
      } else if (rewardScope === 'product_at_a_discount') {
        message.translationKey = 'pricebook.rewards.buy_x_get_y_at_a_discount';
        message.parameters = {
          products: pricebook.reward_products
            .map((product) => `${product.name} - ${product.discount} %`)
            .join(', '),
          min_for_reward: pricebook.min_quantity_for_reward || pricebook.min_value_for_reward,
          min_for_reward_type: pricebook.min_quantity_for_reward ? 'Units' : '€',
        };

        if (pricebook.is_applied_reward && pricebook.applied_discount) {
          message.translationKey = 'pricebook.rewards.buy_x_get_y_at_a_discount_as_applied_reward';
          message.parameters = {
            extra_discount: pricebook.applied_discount,
          };
        }
      } else if (rewardScope === 'more_of_the_same_product_as_a_gift') {
        message.translationKey = 'pricebook.rewards.buy_x_get_y_more_of_the_same_product_as_a_gift';
        message.parameters = {
          quantity: pricebook.product_reward_quantity,
          min_for_reward: pricebook.min_quantity_for_reward || pricebook.min_value_for_reward,
          min_for_reward_type: pricebook.min_quantity_for_reward ? 'Units' : '€',
        };
      } else if (rewardScope === 'more_of_the_same_product_with_an_extra_discount') {
        message.translationKey =
          'pricebook.rewards.buy_x_get_y_more_of_the_same_product_with_an_extra_discount';
        message.parameters = {
          discount: pricebook.product_reward_discount,
          min_for_reward: pricebook.min_quantity_for_reward || pricebook.min_value_for_reward,
          min_for_reward_type: pricebook.min_quantity_for_reward ? 'Units' : '€',
        };

        if (pricebook.is_applied_reward && pricebook.applied_discount) {
          message.translationKey = 'pricebook.rewards.buy_x_get_y_at_a_discount_as_applied_reward';
          message.parameters = {
            extra_discount: pricebook.applied_discount,
          };
        }
      }
    } else if (type === 'free_shipping') {
      message.translationKey = 'pricebook.rewards.free_shipping';

      if (hasConditionOnOrderValue) {
        message.translationKey = 'pricebook.rewards.free_shipping_with_condition';
        message.parameters = {
          min_order_value: pricebook.conditions.find(
            (condition: ICondition) => condition.condition === 'min_order_value',
          )?.condition_value as number,
        };
      }
    }

    return {
      pricebook: pricebook.name,
      message: message.translationKey ? this.translateService.instant(message.translationKey, message.parameters) : '',
      parameters: message.parameters,
    };
  }

  public getGifts(pricebooks: IPriceBookViewDTO[]): { pricebook: string; message: string }[] {
    return pricebooks.map((pricebook) => this.getRewardMessage(pricebook) as { pricebook: string; message: string });
  }
}
